import { z, ZodEffects, ZodType, ZodTypeDef } from "zod";

export const LiteralSchema = z.union([z.string(), z.number(), z.boolean()]);

export type LiteralType = z.infer<typeof LiteralSchema>;

export const JsonSchema: z.ZodType<JsonType> = z.lazy(() =>
  z.union([LiteralSchema, z.array(JsonSchema), z.record(JsonSchema)])
);

export const castToNumber = z.preprocess(
  (val: unknown) => (typeof val === "string" ? Number(val) : val),
  z.number().or(z.string())
);

export const castToString = z.preprocess(
  (val: unknown) => (typeof val === "number" ? val.toString() : val),
  z.string()
);

export const castToBool = z.preprocess(
  (val: unknown) => (typeof val === "string" ? stringToBoolean(val) : val),
  z.boolean()
);

export const nullishDefault = <T>(
  schema: z.ZodType<T>,
  defaultValue: T
): ZodEffects<ZodType<T, ZodTypeDef, T>, T, unknown> =>
  z.preprocess((val: unknown) => val ?? defaultValue, schema);

export const castToCommaList = z.preprocess(
  (arg: unknown) => (Array.isArray(arg) ? arg.join(",") : arg),
  z.string()
);

export const commaListToArray = z.preprocess((arg: unknown) => {
  if (arg === null || arg === undefined) {
    return [];
  }
  return Array.isArray(arg)
    ? arg
    : typeof arg === "string"
      ? arg.split(",")
      : arg;
}, z.string().array());

export type JsonType = LiteralType | { [key: string]: JsonType } | JsonType[];

const stringToBoolean = (value: string): boolean => {
  value = value.toLowerCase();
  if (["y", "yes", "t", "true", "on", "1"].includes(value)) return true;
  if (["n", "no", "f", "false", "off", "0"].includes(value)) return false;
  throw Error(`Invalid truth value ${value}`);
};

export const validateIdNotExponent = z.preprocess((value: unknown) => {
  if (!value) {
    return value;
  }

  if (typeof value === "number") {
    return value.toString();
  }

  if (typeof value === "string" && value.toUpperCase().includes("E")) {
    throw Error("ID Field(s) are in exponential notation.  Please fix IDs.");
  }

  return value;
}, z.string());

export type RecursivePartial<T> = {
  [P in keyof T]?: T[P] extends (infer U)[]
    ? RecursivePartial<U>[]
    : T[P] extends object
      ? RecursivePartial<T[P]>
      : T[P];
};

export type PathsToStringProps<T> = T extends string
  ? []
  : {
      [K in Extract<keyof T, string>]: [K, ...PathsToStringProps<T[K]>];
    }[Extract<keyof T, string>];

export type Join<T extends string[], D extends string> = T extends []
  ? never
  : T extends [infer F]
    ? F
    : T extends [infer F, ...infer R]
      ? F extends string
        ? `${F}${D}${Join<Extract<R, string[]>, D>}`
        : never
      : string;

// @ts-ignore
export type DottedJoin<T> = Join<PathsToStringProps<T>, ".">;

export type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>;

export type WithRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] };
export type Nullable<T> = { [K in keyof T]: T[K] | null };

export type CamelToSnakeCase<S extends string> =
  S extends `${infer T}${infer U}`
    ? T extends Capitalize<T>
      ? `_${Lowercase<T>}${CamelToSnakeCase<U>}`
      : `${T}${CamelToSnakeCase<U>}`
    : S;

export type ConvertKeysToSnakeCase<T> = {
  [K in keyof T as CamelToSnakeCase<string & K>]: T[K] extends Record<
    string,
    any
  >
    ? ConvertKeysToSnakeCase<T[K]>
    : T[K];
};

export type Flatten<T> = {
  [K in keyof T]: T[K] extends object ? Flatten<T[K]> : T[K];
}[keyof T];
